/**
 * DashboardAssetTile — slim Asset-Tile für Dashboard Recent-Uploads.
 *
 * Eingeführt in Slice-2 Redesign (2026-05-09). Pattern aus
 * `design-handoff-8-5-2026/v1/pages-core.jsx` Lines 4–37.
 *
 * Reines Summary-Tile: Thumbnail + displayFilename + relative-time +
 * uploader-firstname. KEIN Selection (Dashboard ist kein Workbench), KEIN
 * Hover-Quick-Actions, KEIN Status-Badge, KEIN Filesize. Click navigiert
 * zu `/assets/:id` (gleiches Wouter-Pattern wie Library AssetCard).
 *
 * Asset-Type-Override für `logo`: kein thumbUrl-render (SVG-XSS-Schutz
 * analog AssetDetailPage), stattdessen Fallback-Tile mit Brand-Color.
 */
import { Link } from 'wouter';
import type { AssetListItem } from '../lib/asset-types';
import { formatRelative } from '../lib/format-relative';
import { I } from '../lib/icons';

function extBadge(filename: string, mime: string): string {
  const dot = filename.lastIndexOf('.');
  if (dot >= 0) {
    const ext = filename.slice(dot + 1).toUpperCase();
    if (ext.length <= 4) return ext;
  }
  const mimeMap: Record<string, string> = {
    'image/jpeg': 'JPG',
    'image/png': 'PNG',
    'image/webp': 'WEBP',
    'image/gif': 'GIF',
    'image/avif': 'AVIF',
    'image/svg+xml': 'SVG',
    'video/mp4': 'MP4',
    'video/quicktime': 'MOV',
    'application/pdf': 'PDF',
  };
  return mimeMap[mime.toLowerCase()] ?? 'FILE';
}

const FALLBACK_BG: Record<AssetListItem['assetType'], { bg: string; fg: string }> = {
  img: { bg: 'var(--color-brand-green-200)', fg: 'var(--color-brand-green-800)' },
  video: { bg: 'var(--color-asset-video-bg)', fg: 'var(--color-brand-yellow)' },
  pdf: { bg: 'var(--color-asset-pdf-bg)', fg: 'var(--color-brand-green-700)' },
  logo: { bg: 'var(--color-brand-green-700)', fg: 'var(--color-brand-yellow)' },
};

type Props = {
  asset: AssetListItem;
};

export function DashboardAssetTile({ asset }: Props) {
  const badge = extBadge(asset.displayFilename, asset.mimeType);
  const useFallback = asset.assetType === 'logo' || !asset.thumbUrl;
  const fallback = FALLBACK_BG[asset.assetType];
  const firstName = asset.uploaderName ? asset.uploaderName.split(' ')[0] : null;

  return (
    <Link
      href={`/assets/${encodeURIComponent(asset.id)}`}
      className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700 rounded-lg"
    >
      <div className="aspect-square mb-2 rounded-lg overflow-hidden border border-ink-100 bg-ink-50 relative">
        {useFallback ? (
          <div
            className="w-full h-full flex items-center justify-center font-mono text-xs2 tracking-wide"
            style={{ backgroundColor: fallback.bg, color: fallback.fg }}
          >
            <span
              className="px-1.5 py-0.5 rounded"
              style={{ backgroundColor: 'rgba(0,0,0,0.18)' }}
            >
              {badge}
            </span>
          </div>
        ) : (
          <img
            src={asset.thumbUrl ?? undefined}
            alt={asset.displayFilename}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        )}
        {/* Play-Overlay analog AssetCard: seit echte Poster-Frames existieren,
            wären Videos im Dashboard sonst nicht von Fotos unterscheidbar. */}
        {asset.assetType === 'video' && (
          <div className="pointer-events-none absolute inset-0 grid place-items-center">
            <span className="grid place-items-center rounded-full bg-brand-green-900/55 p-1.5 text-white shadow-sm">
              <I.play size={16} aria-hidden />
            </span>
          </div>
        )}
      </div>
      {/* Filename-Styling 1:1 wie die Library-Grid-Card (AssetCard.tsx):
          text-xs · font-semibold · brand-green-700 — beide Grid-Tiles teilen
          sich denselben Look (User-Wunsch 2026-06-12). */}
      <div
        className="text-xs font-semibold text-brand-green-700 truncate leading-tight"
        title={asset.displayFilename}
      >
        {asset.displayFilename}
      </div>
      <div className="text-xs2 text-brand-green-600 mt-0.5 truncate">
        {formatRelative(asset.createdAt)}
        {firstName && (
          <>
            <span className="text-brand-green-300 mx-1">·</span>
            {firstName}
          </>
        )}
      </div>
    </Link>
  );
}
